import ko from "knockout";
import _ from "lodash";
import ViewModel from "viewModels/ViewModel";

/**
 * A pager widget to provide the interaction of changing the page of a collection they
 * are viewing.
 *
 * @param [opts.allowedPageSizes=[25, 50, 100] - The page size options to display to
 *                                               the end user.
 */
const Pager = ViewModel.extend(function (opts) {
    const self = this;

    // ===============================================================================
    // Private Variables
    // ===============================================================================

    const options = _.assign(
        {
            allowedPageSizes: [25, 50, 100],
        },
        opts
    );

    let timerObj;
    const currentPage = ko.observable(1);
    const _pageCount = ko.observable(1);

    self.displayedPageNumber = ko.observable(1);

    const delayPageNumberUpdate = function () {
        clearTimeout(timerObj);

        timerObj = setTimeout(function updatePageNumber() {
            self.pageNumber(self.displayedPageNumber());
        }, 200);
    };

    // ===============================================================================
    // Cleanup
    // ===============================================================================

    self.onDispose = function () {
        clearTimeout(timerObj);
    };

    // ===============================================================================
    // Public API
    // ===============================================================================

    /**
     * The current page that the user has selected.
     *
     * @return Integer
     */
    self.pageNumber = self.computed({
        read: currentPage,

        write: function (value) {
            let userSetValue = parseInt(value, 10);

            if (_.isNaN(userSetValue)) {
                const pageCache = currentPage.peek();
                currentPage.notifySubscribers(pageCache);
                self.displayedPageNumber.notifySubscribers(pageCache);
                userSetValue = pageCache;
            }

            if (self.hasPageCount() && userSetValue > self.pageCount()) {
                userSetValue = self.pageCount();
            } else if (userSetValue < 1) {
                userSetValue = 1;
            }

            currentPage(userSetValue);
            self.displayedPageNumber(userSetValue);
        },
    });

    self.hasPageCount = ko.observable(true);

    /**
     * The amount of pages in the collection
     *
     * @return Integer
     */
    self.pageCount = self.computed({
        read: _pageCount,

        write: function (newValue) {
            let pageCountValue = newValue;

            if (newValue < 1) {
                // The server can return a pageCount of 0 on an empty grid but that is
                // never what we want to display.
                //
                // Set it to 1 when that happens.
                pageCountValue = 1;
            }

            _pageCount(pageCountValue);
        },
    });

    /**
     * The number of items displayed in a single page.
     *
     * @return Integer
     */
    self.pageSize = ko.observable(options.allowedPageSizes[0]);

    // ===============================================================================
    // Template Data
    // ===============================================================================

    self.allowedPageSizes = options.allowedPageSizes;

    //
    // Events
    //
    self.nextPageClick = function () {
        const curPage = self.displayedPageNumber();
        const pageCount = self.pageCount();

        if (!self.hasPageCount() || curPage < pageCount) {
            self.displayedPageNumber(curPage + 1);
            delayPageNumberUpdate();
        }
    };

    self.prevPageClick = function () {
        const curPage = self.displayedPageNumber();

        if (curPage > 1) {
            self.displayedPageNumber(curPage - 1);
            delayPageNumberUpdate();
        }

        return false;
    };
});

export default Pager;
